import React, { useEffect, useState } from 'react';
import { Check, ChevronUp, ChevronDown, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { COPY, CATEGORY_LABELS, categoryBg, DEFAULT_COACH_NAME } from './copy';
import { compactMoney } from './format';
import ProposedActions from './ProposedActions';
import { impactOf, impactSummary, rangeAcross, IMPACT_LABELS } from '../shared/impact';

const STATUS_STYLES = {
  open: { label: 'Open', className: 'border-border bg-transparent text-muted-foreground' },
  in_progress: { label: 'In progress', className: 'border-succeedo-orange/30 bg-succeedo-orange/10 text-orange-ink' },
  completed: { label: 'Completed', className: 'border-[hsl(var(--chart-2)/0.3)] bg-[hsl(var(--chart-2)/0.1)] text-teal-ink' },
  blocked: { label: 'Blocked', className: 'border-[hsl(var(--coral)/0.3)] bg-[hsl(var(--coral)/0.1)] text-coral-ink' },
  wont_do: { label: "Won't do", className: 'border-border bg-transparent text-muted-foreground' },
};

const NEXT_STATUS = { open: 'in_progress', in_progress: 'completed', completed: 'open', blocked: 'in_progress', wont_do: 'open' };

function daysLeft(endOn) {
  if (!endOn) return null;
  const diff = Math.ceil((new Date(endOn) - new Date()) / 86400000);
  return Math.max(0, diff);
}

function ActionRow({ item, onAdvance, justAdded }) {
  const status = String(item.status || 'open').toLowerCase();
  const style = STATUS_STYLES[status] || STATUS_STYLES.open;
  const category = item.metadata?.category;
  const impact = impactSummary(impactOf(item));
  const done = status === 'completed';
  return (
    <div
      className={`flex gap-3 p-3.5 border border-border rounded-xl bg-card ${justAdded ? 'border-l-[3px] border-l-succeedo-orange' : ''}`}
    >
      <button
        type="button"
        onClick={() => onAdvance(item, NEXT_STATUS[status] || 'in_progress')}
        aria-label={done ? `Mark "${item.title}" as not done` : `Update "${item.title}" (now ${style.label})`}
        className={`w-[22px] h-[22px] shrink-0 rounded-full flex items-center justify-center text-white mt-px cursor-pointer ${
          done ? 'border-0 bg-[hsl(var(--chart-2))]' : 'border-[1.5px] border-muted-foreground/60 bg-transparent'
        }`}
      >
        {done && <Check size={12} strokeWidth={3} />}
        {status === 'in_progress' && <span className="w-2 h-2 rounded-full bg-succeedo-orange" />}
      </button>
      <div className="flex flex-col flex-1 min-w-0 gap-[7px]">
        <span className={`text-[14px] leading-[1.4] ${done ? 'text-muted-foreground line-through' : 'text-foreground no-underline'}`}>
          {item.title}
        </span>
        <div className="flex items-center flex-wrap gap-2">
          {category && (
            <span className="inline-flex items-center gap-[5px] font-label text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              <span className={`w-1.5 h-1.5 rounded-full ${categoryBg(category)}`} />
              {CATEGORY_LABELS[category] || category}
            </span>
          )}
          {impact ? (
            <span className="font-label text-[11px] text-teal-ink tabular-nums" title={impact.detail || impact.label}>
              {impact.figure} · {impact.short}
            </span>
          ) : null}
          <span
            className={`ml-auto font-label text-[9.5px] tracking-[0.06em] uppercase py-0.5 px-[7px] rounded-full border ${style.className}`}
          >
            {style.label}
          </span>
        </div>
        {justAdded && (
          <span className="font-label text-[10px] uppercase tracking-[0.06em] text-orange-ink">Just added from your conversation</span>
        )}
      </div>
    </div>
  );
}

// Before the owner accepts anything (D01): what will appear here, with
// faint placeholder rows in place of real actions.
function EmptyState({ coachName }) {
  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex flex-col gap-2">
        <span className="font-display text-[22px] font-light leading-[1.25] tracking-[-0.02em] text-foreground">
          {COPY.actionsEmptyHeading}
        </span>
        <span className="text-[13px] leading-[1.6] text-muted-foreground">{COPY.actionsEmpty(coachName)}</span>
      </div>
      <div aria-hidden="true" className="flex flex-col gap-2">
        <div className="h-[58px] border border-dashed border-border rounded-xl flex items-center gap-3 px-3.5">
          <span className="w-[18px] h-[18px] rounded-full border-[1.5px] border-dashed border-muted-foreground/40" />
          <span className="flex-1 flex flex-col gap-1.5">
            <span className="h-1.5 w-[70%] rounded-full bg-muted" />
            <span className="h-1.5 w-[40%] rounded-full bg-muted/60" />
          </span>
        </div>
        <div className="h-[58px] border border-dashed border-border rounded-xl opacity-60" />
        <div className="h-[58px] border border-dashed border-border rounded-xl opacity-30" />
      </div>
    </div>
  );
}

// The coach's proposals still waiting for the owner, above the plan.
function Suggested({ suggestions, onAccept, onDismiss, coachName }) {
  return (
    <section aria-label={COPY.suggestedHeading(coachName)} className="flex flex-col gap-2.5">
      <div className="flex justify-between items-baseline">
        <span className="font-label text-[11px] uppercase tracking-[0.06em] text-orange-ink">{COPY.suggestedHeading(coachName)}</span>
        <span className="font-label text-[11px] text-muted-foreground tabular-nums">{suggestions.length}</span>
      </div>
      <ProposedActions proposals={suggestions} onAccept={onAccept} onDismiss={onDismiss} narrow />
    </section>
  );
}

function PanelBody({ items, suggestions, sprint, onAdvance, onAccept, onDismiss, justAddedIds, coachName }) {
  const total = items.length;
  const completed = items.filter((i) => String(i.status).toLowerCase() === 'completed').length;
  const left = daysLeft(sprint?.endOn);
  const range = rangeAcross(
    items.filter((i) => !['completed', 'wont_do'].includes(String(i.status).toLowerCase())).map((i) => impactOf(i)),
  );
  const suggested = suggestions.length > 0 && (
    <Suggested suggestions={suggestions} onAccept={onAccept} onDismiss={onDismiss} coachName={coachName} />
  );
  if (total === 0) {
    if (!suggested) return <EmptyState coachName={coachName} />;
    return (
      <div className="flex flex-col gap-4">
        {suggested}
        <span className="text-[13px] leading-[1.6] text-muted-foreground">{COPY.suggestedHint}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {suggested}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-baseline">
            <span className="font-label text-[11px] uppercase tracking-[0.06em] text-muted-foreground">Your 90-day plan</span>
            {left !== null && (
              <span className="font-label text-[10px] uppercase tracking-[0.06em] text-muted-foreground">{left} days left</span>
            )}
          </div>
          <div
            role="progressbar"
            aria-valuenow={completed}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-label="Actions completed"
            className="h-1.5 rounded-full bg-border overflow-hidden"
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-succeedo-orange to-[hsl(var(--succeedo-orange-light))]"
              style={{ width: `${(completed / total) * 100}%` }}
            />
          </div>
          <span className="text-[13px] text-muted-foreground tabular-nums">
            {completed} of {total} done
            {range ? (
              <span title={IMPACT_LABELS.range}>{` · roughly ${compactMoney(range[0])}–${compactMoney(range[1])} still in reach (${IMPACT_LABELS.range.toLowerCase()})`}</span>
            ) : ''}
          </span>
        </div>
        <div className="flex flex-col gap-2.5">
          {items.map((item) => (
            <ActionRow key={item.id} item={item} onAdvance={onAdvance} justAdded={justAddedIds.includes(String(item.id))} />
          ))}
        </div>
        <a href="/succeedo/action-plan" className="text-[13px] text-orange-ink no-underline">Open my action plan →</a>
      </div>
    </div>
  );
}

// Whether the desktop rail starts open. Wide screens (xl, 1280px+) have room
// for the conversation and the rail side by side, so it opens; between lg
// and xl (1024-1279px, e.g. an iPad Air in landscape) it starts collapsed
// so the conversation keeps its width. Below lg the rail is a bottom sheet.
// An explicit toggle is remembered per size, so collapsing it on a tablet
// doesn't collapse it on a desktop.
const WIDE_QUERY = '(min-width: 1280px)';
const storageKey = (wide) => `succeedo_actions_rail_${wide ? 'wide' : 'medium'}`;

function isWide() {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(WIDE_QUERY).matches;
}

function savedOpen(wide) {
  try {
    const value = window.localStorage.getItem(storageKey(wide));
    return value === null ? null : value === 'open';
  } catch (e) {
    return null;
  }
}

function useRailOpen() {
  const [wide, setWide] = useState(isWide);
  const [open, setOpen] = useState(() => savedOpen(isWide()) ?? isWide());

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(WIDE_QUERY);
    const onChange = (event) => {
      setWide(event.matches);
      setOpen(savedOpen(event.matches) ?? event.matches);
    };
    // Safari < 14 only has addListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(storageKey(wide), next ? 'open' : 'closed');
    } catch (e) {
      // Storage unavailable (private mode): the choice lasts for this page.
    }
  };

  // Opened from the conversation ("Review in Actions"): for now, not a
  // remembered choice.
  const reveal = () => setOpen(true);

  return [open, toggle, reveal];
}

const LARGE_QUERY = '(min-width: 1024px)';

// The persistent Actions panel (PRD §6.5): a collapsible right rail on
// desktop, a bottom sheet with a peek handle ("3 actions · 1 done") on
// mobile. The coach's proposals wait at the top ("Suggested by George")
// until the owner accepts or dismisses them. Collapsed, the rail is a
// narrow strip with the action count and a dot when something was just
// added or is waiting; the width animates between the two. Each change of
// revealKey opens it (the conversation's "Review in Actions").
export default function ActionsPanel({
  items = [], suggestions = [], sprint, onAdvance, onAccept, onDismiss, justAddedIds = [], coachName = DEFAULT_COACH_NAME,
  revealKey = 0,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [railOpen, toggleRail, revealRail] = useRailOpen();
  const total = items.length;
  const completed = items.filter((i) => String(i.status).toLowerCase() === 'completed').length;
  const waiting = suggestions.length;
  const body = (
    <PanelBody
      items={items}
      suggestions={suggestions}
      sprint={sprint}
      onAdvance={onAdvance}
      onAccept={onAccept}
      onDismiss={onDismiss}
      justAddedIds={justAddedIds}
      coachName={coachName}
    />
  );

  useEffect(() => {
    if (!revealKey) return;
    if (window.matchMedia && window.matchMedia(LARGE_QUERY).matches) revealRail();
    else setSheetOpen(true);
  }, [revealKey]);

  return (
    <>
      <aside
        aria-label="Your actions"
        className={`hidden lg:flex relative shrink-0 overflow-hidden border-l border-border bg-card/50 transition-[width] duration-300 ease-out motion-reduce:transition-none ${
          railOpen ? 'w-[320px]' : 'w-14'
        }`}
      >
        {/* Collapsed strip. Both layers stay rendered and cross-fade
            (display can't be transitioned); `invisible` takes the hidden
            one out of the tab order and the accessibility tree. */}
        <div
          className={`absolute inset-y-0 left-0 flex flex-col items-center w-14 gap-2 py-3.5 transition-[opacity,visibility] duration-200 motion-reduce:transition-none ${
            railOpen ? 'opacity-0 invisible' : 'opacity-100 visible delay-100'
          }`}
        >
          <button
            type="button"
            onClick={toggleRail}
            aria-expanded={false}
            aria-controls="george-actions-rail"
            aria-label={total || waiting ? `Show actions (${total}${waiting ? `, ${waiting} suggested` : ''})` : 'Show actions'}
            title="Show actions"
            className="relative w-9 h-9 rounded-md border-0 bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground flex items-center justify-center cursor-pointer"
          >
            <PanelRightOpen size={18} />
            {(justAddedIds.length > 0 || waiting > 0) && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-succeedo-orange shadow-[0_0_0_2px_hsl(var(--background))]" />
            )}
          </button>
          {total > 0 && <span className="font-label text-[11px] text-muted-foreground tabular-nums">{total}</span>}
        </div>

        {/* Open rail: fixed width so the content doesn't reflow while the
            width animates; the aside clips it like a drawer. */}
        <div
          id="george-actions-rail"
          className={`flex flex-col w-[320px] shrink-0 transition-[opacity,visibility] duration-300 motion-reduce:transition-none ${
            railOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
        >
          <div className="flex items-center justify-between py-[18px] px-5 border-b border-border">
            <div className="flex items-center gap-2.5">
              <span className="text-[15px] font-semibold text-foreground">Actions</span>
              {total > 0 && <span className="font-label text-[11px] text-muted-foreground tabular-nums">{total}</span>}
            </div>
            <button
              type="button"
              onClick={toggleRail}
              aria-expanded
              aria-controls="george-actions-rail"
              aria-label="Hide actions"
              title="Hide actions"
              className="w-8 h-8 -mr-1.5 rounded-md border-0 bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground flex items-center justify-center cursor-pointer"
            >
              <PanelRightClose size={18} />
            </button>
          </div>
          <div className={`flex-1 min-h-0 overflow-y-auto px-5 ${total || waiting ? 'py-[18px]' : 'py-7'}`}>
            {body}
          </div>
        </div>
      </aside>

      <div className="lg:hidden fixed inset-x-0 bottom-0 z-40">
        <div className="bg-card border-t border-border rounded-t-2xl shadow-[0_-8px_24px_rgb(0_0_0/0.08)]">
          <button
            type="button"
            aria-expanded={sheetOpen}
            onClick={() => setSheetOpen(!sheetOpen)}
            className="flex items-center justify-between w-full min-h-12 py-1.5 px-5 bg-transparent border-0 cursor-pointer"
          >
            <span className="flex flex-col items-start gap-1">
              <span className="w-9 h-1 rounded-full bg-border self-center" />
              <span className="text-[14px] font-medium">
                {total} {total === 1 ? 'action' : 'actions'} · {completed} done
                {waiting > 0 && <span className="text-orange-ink"> · {waiting} suggested</span>}
              </span>
            </span>
            {sheetOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </button>
          {sheetOpen && (
            <div className="max-h-[60vh] overflow-y-auto pt-1 px-5 pb-6">
              {body}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
